import React from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Home, LogOut, UtensilsCrossed } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrency } from "@/hooks/useCurrency";
import { useLocale } from "@/contexts/LocaleProvider";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import BranchCounterSelectors from "@/components/layout/BranchCounterSelectors";
import PermissionGate from "@/components/auth/PermissionGate";
import ConsoleAccessBanner from "@/components/layout/ConsoleAccessBanner";
import { cn } from "@/lib/utils";
import { isMultiBranchFeatureEnabled } from "@/lib/permissions";
import "@/styles/simple-ui.css";

export default function SimpleAppShell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { isRtl } = useLocale();
  useCurrency();

  const companyName = user?.company?.name || t("app.title");
  const showBranchBar = isMultiBranchFeatureEnabled(user);
  const isHome = pathname === "/" || pathname === "";
  const isSimpleNative =
    isHome
    || pathname === "/operations"
    || pathname === "/pos"
    || pathname.startsWith("/pos/")
    || pathname === "/tables"
    || pathname === "/kds"
    || pathname === "/waiter"
    || pathname === "/masters"
    || pathname.startsWith("/masters/")
    || pathname === "/reports"
    || pathname.startsWith("/reports/")
    || pathname === "/admin"
    || pathname.startsWith("/admin/");

  return (
    <PermissionGate>
      <div
        className="simple-ui flex h-screen flex-col overflow-hidden"
        dir={isRtl ? "rtl" : "ltr"}
        data-testid="simple-app-shell"
      >
        <ConsoleAccessBanner />
        {!isSimpleNative ? (
          <header className="simple-header print:hidden shrink-0 shadow-md">
            <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-3 px-4 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-white/20">
                  <UtensilsCrossed className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="truncate text-[17px] font-bold leading-tight">{companyName}</div>
                  <div className="truncate text-[12px] text-white/80">
                    {user?.name}
                    {user?.default_branch?.name ? ` · ${user.default_branch.name}` : ""}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate("/")}
                  className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-white/15 px-3 text-[13px] font-semibold hover:bg-white/25"
                  data-testid="simple-home-btn"
                >
                  <Home className="h-4 w-4" />
                  {t("common.home")}
                </button>
                <LanguageSwitcher className="h-10 border-white/30 bg-white/15 text-white hover:bg-white/25" />
                <button
                  type="button"
                  onClick={() => logout()}
                  className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-white/15 px-3 text-[13px] font-semibold hover:bg-white/25"
                  data-testid="simple-logout-btn"
                >
                  <LogOut className="h-4 w-4" />
                  {t("common.signOut")}
                </button>
              </div>
            </div>
            {showBranchBar ? (
              <div className="border-t border-white/10 bg-black/10 px-4 py-2">
                <div className="mx-auto max-w-[1400px]">
                  <BranchCounterSelectors className="!flex flex-wrap" showCounter={false} />
                </div>
              </div>
            ) : null}
          </header>
        ) : null}

        <main
          className={cn(
            "min-h-0 flex-1",
            isSimpleNative ? "overflow-hidden flex flex-col" : "overflow-y-auto",
          )}
          data-testid="simple-main"
        >
          {isSimpleNative ? (
            <Outlet />
          ) : (
            <div className="flex min-h-0 flex-1 flex-col bg-[#EFE6DE]">
              <div className="flex items-center gap-2 border-b border-[#E8D5C8] bg-white px-3 py-2 print:hidden">
                <Link
                  to="/"
                  className="inline-flex items-center gap-1 rounded-lg bg-violet-50 px-2.5 py-1.5 text-[12px] font-semibold text-violet-700"
                >
                  <Home className="h-3.5 w-3.5" />
                  {t("simple.backHome")}
                </Link>
                <span className="text-[12px] text-slate-500">{t("simple.classicScreenHint")}</span>
              </div>
              <div className="min-h-0 flex-1 overflow-auto">
                <Outlet />
              </div>
            </div>
          )}
        </main>
      </div>
    </PermissionGate>
  );
}
